/**
 * On-disk cooldown ledger for spam reports.
 *
 * Threat model
 * ============
 *
 * The cooldown ledger is a defence-in-depth measure against a hostile
 * (or buggy) UI flooding the abuse pipeline. Specifically it prevents:
 *
 *   - A compromised "Report" button that fires N reports per second
 *     against the same peer.
 *   - A pranking sibling who taps "Report" 100 times in five seconds.
 *   - A future automation harness that loops over a contact list and
 *     fires reports without rate limiting.
 *
 * The cooldown is enforced LOCALLY. The server is expected to also
 * rate-limit; if it does not, the local ledger still bounds the noise
 * this device can produce. We do NOT rely on the local ledger for
 * security against a sophisticated adversary who has rooted the device
 * — that adversary can erase the store. We rely on it to keep an honest
 * device honest.
 *
 * Storage
 * -------
 *
 * IndexedDB, one record per reportedUserId, keyed by the SHA-256 hex of
 * the reportedUserId. The hashed key means a forensic snapshot of the
 * store's key-list does not reveal which userIds the user has reported
 * (only how many peers they have reported overall).
 *
 * The record stores a sliding window of recent report timestamps. The
 * window is 10 seconds wide and admits up to 10 reports — outside the
 * window, timestamps age off and capacity is restored.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/SpamReportStore.swift`.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Cooldown window in seconds. Reports older than this drop off. */
export const SPAM_REPORT_COOLDOWN_WINDOW_SECONDS = 10;

/** Maximum number of reports allowed within the cooldown window per peer. */
export const SPAM_REPORT_MAX_IN_WINDOW = 10;

// ---------------------------------------------------------------------------
// SpamReportRecord
// ---------------------------------------------------------------------------

/**
 * Persisted record for a single reportedUserId.
 * Mirrors `SpamReportRecord` in `kaether_for_ios/Stoat/E2EE/SpamReportStore.swift`.
 */
export interface SpamReportRecord {
  /**
   * Hashed key (SHA-256 hex of the reportedUserId). This is the IDB keyPath
   * so we don't store the raw userId as the record key.
   */
  readonly hashedKey: string;
  /** Total reports ever filed against this peer (monotonically increasing). */
  readonly count: number;
  /**
   * Unix-seconds timestamps of reports within the sliding cooldown window.
   * Pruned on each write to remove entries outside the window.
   */
  readonly recentTimestamps: readonly number[];
}

// ---------------------------------------------------------------------------
// IndexedDB plumbing
// ---------------------------------------------------------------------------

const DB_VERSION = 1;
const STORE_NAME = "spam_reports";

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "hashedKey" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGet(
  db: IDBDatabase,
  hashedKey: string,
): Promise<SpamReportRecord | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(hashedKey);
    req.onsuccess = () =>
      resolve(req.result as SpamReportRecord | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db: IDBDatabase, record: SpamReportRecord): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function idbClear(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// Key hashing
// ---------------------------------------------------------------------------

/**
 * SHA-256 hex digest of the reportedUserId UTF-8 bytes.
 * Used as the IDB record key so the key-list does not reveal userId values.
 */
async function hashKey(userId: string): Promise<string> {
  const bytes = new TextEncoder().encode(userId);
  const hashBuf = await crypto.subtle.digest("SHA-256", bytes);
  const arr = new Uint8Array(hashBuf);
  return Array.from(arr)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ---------------------------------------------------------------------------
// SpamReportStore
// ---------------------------------------------------------------------------

const SHARED_DB_NAME = "kaether:spam-reports:v1";

/**
 * IndexedDB-backed sliding-window cooldown ledger for spam reports.
 *
 * All methods are async because every read/write goes through IndexedDB.
 * For test isolation, pass a unique `dbName` per test case.
 *
 * Mirrors `SpamReportStore` in `kaether_for_ios/Stoat/E2EE/SpamReportStore.swift`.
 */
export class SpamReportStore {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName: string = SHARED_DB_NAME) {
    this.dbName = dbName;
  }

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDb(this.dbName);
    }
    return this.dbPromise;
  }

  // -------------------------------------------------------------------------
  // Cooldown queries
  // -------------------------------------------------------------------------

  /**
   * Returns true iff a fresh report against `reportedUserId` would NOT
   * exceed the sliding-window quota.
   *
   * `nowSeconds` defaults to the current Unix time.
   *
   * Mirrors `SpamReportStore.canReport(reportedUserId:at:)` in iOS.
   */
  async canReport(
    reportedUserId: string,
    nowSeconds: number = Math.floor(Date.now() / 1000),
  ): Promise<boolean> {
    const db = await this.db();
    const key = await hashKey(reportedUserId);
    const existing = await idbGet(db, key);
    const recent = pruneWindow(
      existing?.recentTimestamps ?? [],
      nowSeconds,
    );
    return recent.length < SPAM_REPORT_MAX_IN_WINDOW;
  }

  /**
   * Records a report observation. Returns true if recorded; false if
   * the cooldown rejected the call (no state change on rejection).
   *
   * `nowSeconds` defaults to the current Unix time.
   *
   * Mirrors `SpamReportStore.recordReport(reportedUserId:at:)` in iOS.
   */
  async recordReport(
    reportedUserId: string,
    nowSeconds: number = Math.floor(Date.now() / 1000),
  ): Promise<boolean> {
    const db = await this.db();
    const key = await hashKey(reportedUserId);
    const existing = await idbGet(db, key);
    const pruned = pruneWindow(
      existing?.recentTimestamps ?? [],
      nowSeconds,
    );
    if (pruned.length >= SPAM_REPORT_MAX_IN_WINDOW) {
      return false;
    }
    const updated: SpamReportRecord = {
      hashedKey: key,
      count: (existing?.count ?? 0) + 1,
      recentTimestamps: [...pruned, nowSeconds],
    };
    await idbPut(db, updated);
    return true;
  }

  /**
   * Retrieve the stored record for `reportedUserId`, or undefined if none.
   * Mirrors `SpamReportStore.report(reportedUserId:)` in iOS.
   */
  async report(reportedUserId: string): Promise<SpamReportRecord | undefined> {
    const db = await this.db();
    const key = await hashKey(reportedUserId);
    return idbGet(db, key);
  }

  // -------------------------------------------------------------------------
  // Reset
  // -------------------------------------------------------------------------

  /** Drop every stored record. Used in tests and on sign-out. */
  async resetAll(): Promise<void> {
    const db = await this.db();
    await idbClear(db);
  }
}

// ---------------------------------------------------------------------------
// Shared singleton
// ---------------------------------------------------------------------------

/** Shared singleton for production use. Tests use isolated instances. */
export const sharedSpamReportStore = new SpamReportStore();

// ---------------------------------------------------------------------------
// Sliding-window helper (pure, no IDB)
// ---------------------------------------------------------------------------

/**
 * Remove timestamps that are outside the cooldown window.
 * A timestamp `ts` is inside the window iff `nowSeconds - ts < windowSeconds`.
 */
function pruneWindow(
  timestamps: readonly number[],
  nowSeconds: number,
  windowSeconds: number = SPAM_REPORT_COOLDOWN_WINDOW_SECONDS,
): number[] {
  return timestamps.filter((ts) => nowSeconds - ts < windowSeconds);
}
